import { useState, useCallback } from 'react';

interface Achievement {
  id: string;
  name: string;
  description: string;
  icon: string;
  requirement: number;
}

interface UnlockedAchievement extends Achievement {
  timestamp: number;
}

export const useAchievements = (capturedCount: number, totalCount: number) => {
  const [newlyUnlocked, setNewlyUnlocked] = useState<UnlockedAchievement[]>([]);

  const achievements: Achievement[] = [
    {
      id: 'iniciante',
      name: 'Iniciante',
      description: 'Capture seu primeiro Feisty Pet',
      icon: 'fa-solid fa-star',
      requirement: 1
    },
    {
      id: 'colecionador',
      name: 'Colecionador',
      description: 'Capture 10 Feisty Pets',
      icon: 'fa-solid fa-trophy',
      requirement: 10
    },
    {
      id: 'mestre',
      name: 'Mestre',
      description: 'Capture 25 Feisty Pets',
      icon: 'fa-solid fa-crown',
      requirement: 25
    },
    {
      id: 'expert',
      name: 'Expert',
      description: 'Capture 50 Feisty Pets',
      icon: 'fa-solid fa-gem',
      requirement: 50
    },
    {
      id: 'lendario',
      name: 'Lendário',
      description: 'Capture 75 Feisty Pets',
      icon: 'fa-solid fa-fire',
      requirement: 75
    },
    {
      id: 'completista',
      name: 'Completista',
      description: 'Complete 100% da coleção',
      icon: 'fa-solid fa-medal',
      requirement: totalCount
    }
  ];

  const playAchievementSound = useCallback(() => {
    try {
      const audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
      const oscillator = audioContext.createOscillator();
      const gainNode = audioContext.createGain();
      
      oscillator.connect(gainNode);
      gainNode.connect(audioContext.destination);
      
      // Som de conquista (sequência de notas ascendentes)
      const notes = [523.25, 659.25, 783.99, 1046.50]; // C5, E5, G5, C6
      let noteIndex = 0;
      
      const playNote = () => {
        if (noteIndex < notes.length) {
          oscillator.frequency.setValueAtTime(notes[noteIndex], audioContext.currentTime);
          gainNode.gain.setValueAtTime(0.3, audioContext.currentTime);
          gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.2);
          noteIndex++;
          setTimeout(playNote, 150);
        }
      };
      
      oscillator.start();
      playNote();
      
      setTimeout(() => {
        oscillator.stop();
      }, 800);
    } catch (error) {
      console.log('Não foi possível reproduzir o som de conquista');
    }
  }, []);

  const checkForNewAchievements = useCallback((previousCount: number, currentCount: number) => {
    const newAchievements: UnlockedAchievement[] = [];
    
    achievements.forEach(achievement => {
      const isUnlocked = achievement.id === 'completista' 
        ? currentCount === totalCount && totalCount > 0
        : currentCount >= achievement.requirement;
      
      const wasUnlocked = achievement.id === 'completista'
        ? previousCount === totalCount && totalCount > 0
        : previousCount >= achievement.requirement;
      
      // Só adiciona se acabou de desbloquear (não estava desbloqueado antes)
      if (isUnlocked && !wasUnlocked) {
        newAchievements.push({
          ...achievement,
          timestamp: Date.now()
        });
      }
    });

    if (newAchievements.length > 0) {
      setNewlyUnlocked(prev => [...prev, ...newAchievements]);
      playAchievementSound();
    }
  }, [totalCount, playAchievementSound]);

  const dismissAchievement = useCallback((achievementId: string) => {
    setNewlyUnlocked(prev => prev.filter(achievement => achievement.id !== achievementId));
  }, []);

  return {
    newlyUnlocked,
    dismissAchievement,
    checkForNewAchievements
  };
};
